import { i18n } from "services/i18nService";
import { setPageDirty } from "services/utility/edit";
import { handleProgramError } from "services/utility/errors";

import { EMPTY_LINE_ITEM } from "editConfig";

export function handleDrop(result, lineHandler, lines, setReadOnlyLineIds) {
  const { source, destination } = result;

  // if there's no destination, the item was dropped outside of the
  // droppable area
  if (!destination) {
    return;
  }

  // destination same as source, no-op
  if (destination.index === source.index) {
    return;
  }

  // Update readOnlyLineIds to reflect new positions after drag-and-drop
  if (setReadOnlyLineIds) {
    setReadOnlyLineIds((prev) => {
      const newSet = new Set();

      // Update indices for all lines
      prev.forEach((index) => {
        let newIndex = index;

        if (index === source.index) {
          // The dragged line moves to destination
          newIndex = destination.index;
        } else if (source.index < destination.index) {
          // Dragging down: indices between source and destination shift up
          if (index > source.index && index <= destination.index) {
            newIndex = index - 1;
          }
        } else {
          // Dragging up: indices between destination and source shift down
          if (index >= destination.index && index < source.index) {
            newIndex = index + 1;
          }
        }

        newSet.add(newIndex);
      });

      return newSet;
    });
  }

  // make the move
  const newLines = [...lines];
  const savedSource = newLines.splice(source.index, 1)[0];
  newLines.splice(destination.index, 0, savedSource);

  // renumber and update changed lines
  const start = Math.min(source.index, destination.index);
  const end = Math.max(source.index, destination.index);
  setPageDirty();
  for (let i = start; i <= end; i++) {
    newLines[i].lineNumber = i + 1;
    lineHandler({ type: "update", updatedLine: newLines[i] });
  }
}

/**
 * Creates a function to handle line deletion with proper readOnlyLineIds index management.
 * When a line is deleted, this function:
 * 1. Calls setPageDirty()
 * 2. Calls lineHandler to delete the line
 * 3. Updates readOnlyLineIds Set by removing the deleted index and shifting down all subsequent indices
 *
 * @param {Function} lineHandler - The line handler function
 * @param {Function} setReadOnlyLineIds - State setter for readOnlyLineIds
 * @returns {Function} Function that takes a lineNumber and handles deletion
 */
export function createHandleDeleteLine(lineHandler, setReadOnlyLineIds) {
  return function handleDeleteLine(lineNumber) {
    setPageDirty();
    lineHandler({ type: "delete", deleteAt: lineNumber });

    // Update readOnlyLineIds: remove the deleted line and shift down all indices after it
    setReadOnlyLineIds((prev) => {
      const newSet = new Set();
      prev.forEach((index) => {
        if (index < lineNumber - 1) {
          // Indices before the deleted line stay the same
          newSet.add(index);
        } else if (index > lineNumber - 1) {
          // Indices after the deleted line shift down by 1
          newSet.add(index - 1);
        }
        // index === lineNumber - 1 is the deleted line, so we don't add it
      });
      return newSet;
    });
  };
}

/**
 * Creates a function to handle line insertion with proper readOnlyLineIds index management.
 * When a line is inserted, this function:
 * 1. Calls setPageDirty()
 * 2. Calls lineHandler to insert the line
 * 3. Updates readOnlyLineIds Set by shifting down all indices >= insertAt
 * 4. Ensures the newly inserted line is editable (not in the Set)
 *
 * @param {Function} lineHandler - The line handler function
 * @param {Function} setReadOnlyLineIds - State setter for readOnlyLineIds
 * @param {Object} emptyLineItem - The empty line item template to insert
 * @returns {Function} Function that takes an insertAt position and handles insertion
 */
export function createInsertEmptyLine(
  lineHandler,
  setReadOnlyLineIds,
  emptyLineItem
) {
  return function insertEmptyLine(insertAt) {
    setPageDirty();
    lineHandler({
      type: "insert",
      insertAt: insertAt,
      newLine: emptyLineItem,
    });

    // Update readOnlyLineIds: shift indices >= (insertAt - 1) down, new line is editable
    // insertAt is 1-based (lineNumber), so convert to 0-based index
    const insertIndex = insertAt - 1;
    setReadOnlyLineIds((prev) => {
      const newSet = new Set();
      prev.forEach((index) => {
        if (index >= insertIndex) {
          newSet.add(index + 1); // Shift down indices at/after insert point
        } else {
          newSet.add(index); // Keep indices before insert point
        }
      });
      // The newly inserted line at insertIndex is NOT added (stays editable)
      return newSet;
    });
  };
}

/**
 * Creates a function to make a read-only line editable.
 * Removes the line index from the readOnlyLineIds Set.
 *
 * @param {Function} setReadOnlyLineIds - State setter for readOnlyLineIds
 * @returns {Function} Function that takes a lineIndex and makes it editable
 */
export function createSetLineEdit(setReadOnlyLineIds) {
  return function setLineEdit(lineIndex) {
    setReadOnlyLineIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(lineIndex); // Remove from read-only set to make it editable
      return newSet;
    });
  };
}

export function copyDown(line, lineHandler, objectType) {
  let updatedLine = {
    ...line,
    lineNumber: line.lineNumber + 1,
    serials: [],
    lot: EMPTY_LINE_ITEM[objectType].lot,
    lotExpiration: EMPTY_LINE_ITEM[objectType].lotExpiration,
  };
  delete updatedLine.id;
  setPageDirty();
  lineHandler({ type: "update", updatedLine: updatedLine });
}

export function textForAddLineOrLines(numLinesToAdd) {
  if (numLinesToAdd < 1) {
    handleProgramError(
      new Error(`Unexpected value for numLinesToAdd (${numLinesToAdd})`)
    );
  }
  return numLinesToAdd === 1 ? i18n("global.AddLine") : i18n("global.AddLines");
}
